import * as Yup from "yup";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { CONTEXT_TYPES } from "api/bookAppointmentOnline/get-book-appointment-dashboard-items";
import { getPhoneRegex } from "helpers/get-phone-regex.helper";
import { useNavigate, useLocation } from "react-router";
import { useToast } from "contexts/toast/toast.context";
import { ConsentType } from "models/BookAppointmentOnlineModel/BookAppointmentOnlineDashboard.model";
import bookAppointmentOnline from "api/bookAppointmentOnline/book-appointment-online";
import { AVAILABLE_TIME_SLOTS_QUERY_KEY } from "hooks/bookAppointmentOnline/useGetAppointmentOnlineAvailableSlots.hook";

type ConsentConfig = {
    agreementConsent?: ConsentType;
    personalDataConsent?: ConsentType;
    marketingConsent?: ConsentType;
};

const baseSchema = Yup.object({
    typeId: Yup.string().notRequired(),
    doctorId: Yup.string().notRequired(),
    date: Yup.string().required("Wybierz dzień"),
    time: Yup.string().required("Wybierz godzinę"),
    firstName: Yup.string().required("Uzupełnij imię").max(100),
    lastName: Yup.string().required("Uzupełnij nazwisko").max(100),
    phoneNumber: Yup.string()
        .required("Uzupełnij nr. telefonu")
        .matches(getPhoneRegex(), "Niepoprawny nr telefonu")
        .max(100),
    agreementConsent: Yup.boolean().notRequired(),
    personalDataConsent: Yup.boolean().notRequired(),
    marketingConsent: Yup.boolean().notRequired(),
    addToCalendar: Yup.boolean().notRequired(),
});

export type FormValues = Yup.InferType<typeof baseSchema>;

export const useBookAppointmentOnline = (
    context: CONTEXT_TYPES,
    contextId: string,
    defaultDate: string,
    onSuccess?: () => void,
    consentConfig?: ConsentConfig,
    redirectUrl?: string | null,
    redirectOnClick?: boolean,
) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { addSuccessToast, addFailureToast }  = useToast();
    const [awaitingRedirectClick, setAwaitingRedirectClick] = useState(false);
    const queryClient = useQueryClient();

    const handleRedirectClick = () => {
        if (!redirectUrl) return;

        try {
            const topWindow = window.top;
            if (!topWindow) throw new Error("Brak dostępu do okna nadrzędnego");
            topWindow.location.href = redirectUrl;
        } catch {
            addFailureToast("Nie udało się przekierować. Spróbuj otworzyć stronę ręcznie.");
        }
    };

    const schema = baseSchema.shape({
        typeId:
            (context === CONTEXT_TYPES.CONTEXT_DOCTOR || context === CONTEXT_TYPES.CONTEXT_ALL)
                ? Yup.string().required("Wybierz usługę")
                : Yup.string().notRequired(),
        doctorId: Yup.string().notRequired(),
        agreementConsent: consentConfig?.agreementConsent?.Show
            ? Yup.boolean().oneOf([true], "Zaakceptuj zgodę").required("Zaakceptuj zgodę")
            : Yup.boolean().notRequired(),
        personalDataConsent: consentConfig?.personalDataConsent?.Show
            ? Yup.boolean().oneOf([true], "Zaakceptuj zgodę").required("Zaakceptuj zgodę")
            : Yup.boolean().notRequired(),
        marketingConsent: Yup.boolean().notRequired(),
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
        watch,
        setValue,
        reset,
        getValues,
    } = useForm<FormValues>({
        resolver: yupResolver(schema),
        defaultValues: {
            doctorId:
                context === CONTEXT_TYPES.CONTEXT_DOCTOR ? contextId : undefined,
            typeId:
                context === CONTEXT_TYPES.CONTEXT_SERVICE ? contextId : undefined,
            date: defaultDate,
            agreementConsent: consentConfig?.agreementConsent?.Show ? false : undefined,
            personalDataConsent: consentConfig?.personalDataConsent?.Show ? false : undefined,
            marketingConsent: consentConfig?.marketingConsent?.Show ? false : undefined,
            addToCalendar: false,
        },
    });

    const { mutate, isPending, error, reset: resetMutationError } = useMutation({
        mutationFn: (formData: FormValues) => bookAppointmentOnline(formData),
        onSuccess: (data) => {
            if (redirectUrl && redirectOnClick) {
                setAwaitingRedirectClick(true);
            } else if (redirectUrl) {
                window.location.href = redirectUrl;
            } else {
                const shouldGoToPaymentConfirmation =
                    context !== CONTEXT_TYPES.CONTEXT_ALL ||
                    (data?.payUPaymentAllowed && data?.link);

                if (shouldGoToPaymentConfirmation) {
                    navigate("/book-appointment-online-confirmation#success", { state: {
                            guid: data?.guid,
                            payUPaymentAllowed: data?.payUPaymentAllowed,
                            isPayed: data?.isPayed,
                            link: data?.link,
                            fromWidget: pathname.startsWith("/widget"),
                            returnPath: pathname,
                        }
                    });
                } else {
                    navigate(`${pathname}#success`);
                    addSuccessToast("Zarezerwowano wizytę");
                }
            }

            if (onSuccess) onSuccess();

            queryClient.invalidateQueries({ queryKey: [AVAILABLE_TIME_SLOTS_QUERY_KEY], exact: false });
            queryClient.invalidateQueries({ queryKey: ["availableDaysList"], exact: false });

            const { typeId, doctorId, date } = getValues();
            reset({
                typeId,
                doctorId,
                date,
                time: null,
                firstName: undefined,
                lastName: undefined,
                phoneNumber: undefined,
                agreementConsent: consentConfig?.agreementConsent?.Show ? false : undefined,
                personalDataConsent: consentConfig?.personalDataConsent?.Show ? false : undefined,
                marketingConsent: consentConfig?.marketingConsent?.Show ? false : undefined,
                addToCalendar: false,
            });
        },
        onError: (err: any) => {
            let message = "Wystąpił niespodziewany błąd.";
            if (isAxiosError(err)) {
                message = err.response?.data?.message || err.message || message;
            } else if (err instanceof Error) {
                message = err.message;
            }
            console.log(`Error adding appointment: ${message}`);
        }
    });

    const onSubmit = handleSubmit((data) => {
        mutate(data);
    });

    return {
        onSubmit,
        register,
        loading: isPending,
        error: error?.response?.data?.message || error?.message,
        formErrors: errors,
        control,
        watch,
        setValue,
        reset,
        resetMutationError,
        awaitingRedirectClick,
        handleRedirectClick,
    };
};
